import { User } from 'lucide-react';

interface AvatarProps {
  name: string;
  photoURL?: string;
  size?: number | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
}

const colorPalette = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#a855f7', // purple
  '#f97316', // orange
  '#ec4899', // pink
  '#14b8a6', // teal
  '#eab308', // yellow
  '#ef4444', // red
];

export default function Avatar({ name, photoURL, size = 'md', className = '', onClick }: AvatarProps) {
  // Map size prop to pixel values
  const sizeMap = {
    xs: 24,
    sm: 32,
    md: 40,
    lg: 56,
    xl: 80,
  };

  const pixelSize = typeof size === 'number' ? size : sizeMap[size];
  const fontSize = pixelSize * 0.38;
  const iconSize = pixelSize * 0.5;

  // Generate consistent color from name
  const colorIndex = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const bgColor = colorPalette[colorIndex % colorPalette.length];

  const containerStyle: React.CSSProperties = onClick ? { cursor: 'pointer' } : {};

  if (photoURL) {
    return (
      <div 
        className={`avatar ${className}`} 
        style={{ 
          width: pixelSize, 
          height: pixelSize,
          position: 'relative',
          ...containerStyle
        }}
        onClick={onClick}
      >
        <img
          src={photoURL}
          alt={name}
          style={{ 
            width: '100%', 
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
        <div 
          style={{
            position: 'absolute',
            inset: '-2px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${bgColor}, ${colorPalette[(colorIndex + 3) % colorPalette.length]})`,
            zIndex: -1,
            opacity: 0,
            transition: 'opacity 0.3s ease',
          }}
          className="avatar-ring"
        />
      </div>
    );
  }

  return (
    <div
      className={`avatar avatar--initials ${className}`}
      style={{
        width: pixelSize,
        height: pixelSize,
        fontSize,
        backgroundColor: bgColor,
        position: 'relative',
        ...containerStyle
      }}
      onClick={onClick}
    >
      <User size={iconSize} />
      <div 
        style={{
          position: 'absolute',
          inset: '-2px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${bgColor}, ${colorPalette[(colorIndex + 3) % colorPalette.length]})`,
          zIndex: -1,
          opacity: 0,
          transition: 'opacity 0.3s ease',
        }}
        className="avatar-ring"
      />
    </div>
  );
}
